import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';

import { AcknowledgeUserIdentityConflictAction, ReevaluateUserIdentityConflictAction, ResolveAmbiguousUserIdentityConflictAction, RetryUserIdentityMigrationAction } from '~src/app/actions/spaces/UserIdentityActions';
import { showResolveAmbiguousUserIdentityConflict } from '~src/authentication/actions/ResolveAmbiguousUserIdentityConflictAction';
import { IUserIdentityConflict } from '~src/authentication/requests/types/IUserIdentityConflict';
import { UserIdentityConflictsService } from '~src/authentication/services/UserIdentityConflictsService';
import { ContentActionNotification } from '~src/commonInfra/contentActions/ContentActionNotification';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { reloadSpace } from '~src/spacesPlatform/sagas/content/RefreshContentSagas';

import { Messages } from '../../../codegen/Localize';
import {
  AcknowledgeUserIdentityConflict, ReevaluateUserIdentityConflict, ResolveAmbiguousUserIdentityConflict, RetryUserIdentityMigration
} from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';


// The backend has a limit on 1k records for array arguments. We also don't want to process too much at the same time.
const ACTION_BATCH_SIZE = 998;

function handleResponse(message: string, result: IResult) {
  if (result.errors && result.errors[0]) {
    ContentActionNotification.displayToastForGenericError(result.errors[0]);
  } else {
    ContentActionNotification.displaySuccessToast(message);
  }
}

export function retryUserIdentityMigration(conflicts: IUserIdentityConflict[]) {
  return UserIdentityConflictsService.retryUserIdentityMigration(conflicts.map(x => x.id))
    .then((result) => handleResponse(Messages.identityMigration_RetryMigrationAction_Success(), result));
}

export function* handleRetryUserIdentityMigration(action: RetryUserIdentityMigrationAction): SagaIterator {
  try {
    for (let i = 0; i < action.conflicts.length; i+= ACTION_BATCH_SIZE) {
      yield call(retryUserIdentityMigration, action.conflicts.slice(i, i+ACTION_BATCH_SIZE));
    }

    // Need to reload space to update the page content.
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function acknowledgeUserIdentityConflict(conflicts: IUserIdentityConflict[]) {
  return UserIdentityConflictsService.acknowledgeUserIdentityConflicts(conflicts.map(x => x.id))
    .then((result) => handleResponse(Messages.identityMigration_AcknowledgeUserIdentityConflict_Success(), result));
}

export function* handleAcknowledgeUserIdentityConflict(action: AcknowledgeUserIdentityConflictAction): SagaIterator {
  try {
    for (let i = 0; i < action.conflicts.length; i+= ACTION_BATCH_SIZE) {
      yield call(acknowledgeUserIdentityConflict, action.conflicts.slice(i, i+ACTION_BATCH_SIZE));
    }

    // Need to reload space to update the page content.
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function reevaluateUserIdentityConflict(conflicts: IUserIdentityConflict[]) {
  return UserIdentityConflictsService.reevaluateUserIdentityConflicts(conflicts.map(x => x.id))
    .then((result) => handleResponse(Messages.identityMigration_ReevaluateUserIdentityConflict_Success(), result));
}

export function* handleReevaluateUserIdentityConflict(action: ReevaluateUserIdentityConflictAction): SagaIterator {
  try {
    for (let i = 0; i < action.conflicts.length; i+= ACTION_BATCH_SIZE) {
      yield call(reevaluateUserIdentityConflict, action.conflicts.slice(i, i+ACTION_BATCH_SIZE));
    }

    // Need to reload space to update the page content.
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function resolveAmbiguousUserIdentityConflict(conflict: IUserIdentityConflict) {
  return showResolveAmbiguousUserIdentityConflict(conflict)
    .then((result) => handleResponse(Messages.identityMigration_ResolveAmbiguousUserIdentityConflictAction_Success(), result));
}

export function* handleResolveAmbiguousUserIdentityConflict(action: ResolveAmbiguousUserIdentityConflictAction): SagaIterator {
  try {
    yield call(resolveAmbiguousUserIdentityConflict, action.conflict);

    // Need to reload space to update the page content.
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootUserIdentityActionSaga(): SagaIterator {
  yield takeEvery(RetryUserIdentityMigration, handleRetryUserIdentityMigration);
  yield takeEvery(AcknowledgeUserIdentityConflict, handleAcknowledgeUserIdentityConflict);
  yield takeEvery(ReevaluateUserIdentityConflict, handleReevaluateUserIdentityConflict);
  yield takeEvery(ResolveAmbiguousUserIdentityConflict, handleResolveAmbiguousUserIdentityConflict);
}
